// src/components/common/analytics/StackedBarsChart.tsx
//
// Shared stacked-bar chart for the Dirección analytics pages (Resumen's
// `byDay`, Tiempos' `byHour`/`byWeekday`). Ground rules: thin marks, 2 px
// gaps between bars, legend always visible, a single (never dual) Y axis,
// colours only from the caller's series (theme/channel registry).
import React from 'react';
import { Box, useTheme } from '@mui/material';
import { useTranslate } from 'react-admin';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export interface StackedBarSeries {
  key: string;
  labelKey: string;
  color: string;
}

interface StackedBarsChartProps {
  data: Record<string, any>[];
  xKey: string;
  series: StackedBarSeries[];
  height?: number;
  formatX?: (value: string | number) => string;
  /** `false` renders grouped (side-by-side) bars instead of a stack — for
   *  "this period vs previous period" comparisons, where summing would lie. */
  stacked?: boolean;
  /** Per-bar colour for a single-series chart whose bars are categories with
   *  their own colour (e.g. one bar per channel). Falls back to the series colour. */
  cellColor?: (row: Record<string, any>) => string | undefined;
}

export const StackedBarsChart: React.FC<StackedBarsChartProps> = ({ data, xKey, series, height = 220, formatX, stacked = true, cellColor }) => {
  const theme = useTheme();
  const translate = useTranslate();

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} barGap={2} barCategoryGap="20%">
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: 11 }}
            stroke={theme.palette.text.secondary}
            tickFormatter={formatX}
          />
          <YAxis tick={{ fontSize: 11 }} stroke={theme.palette.text.secondary} allowDecimals={false} />
          <Tooltip
            contentStyle={{
              fontSize: 12,
              borderRadius: theme.custom?.radius?.small ?? 8,
              border: `1px solid ${theme.palette.divider}`,
            }}
            labelFormatter={(value) => (formatX ? formatX(value) : value)}
            formatter={(value, name) => [value, translate(name as string)]}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} formatter={(value) => translate(value as string)} />
          {series.map((s) => (
            <Bar key={s.key} dataKey={s.key} name={s.labelKey} stackId={stacked ? 'stack' : undefined} fill={s.color} maxBarSize={18}>
              {cellColor && data.map((row, i) => <Cell key={i} fill={cellColor(row) ?? s.color} />)}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
